import { Remove, TrendingDown, TrendingUp } from '@mui/icons-material';
import {
  Box,
  CircularProgress,
  Tooltip,
  Typography,
  lighten,
  useTheme,
} from '@mui/material';
import dayjs from 'dayjs';
import { useMemo } from 'react';
import WidgetCard from 'switch-fe-shared/components/Cards/WidgetCard';
import {
  formatDateByBucketUnit,
  formatNumber,
} from 'switch-fe-shared/utils/formatting';

import { useDashboard } from '../../../../context/dashboardContext';

interface TrendData {
  value: number;
  percentageChange: number;
  isIncreasing: boolean;
}

interface MetricCardProps {
  title: string;
  value?: number;
  unit: string;
  trend?: TrendData;
  hideTrends?: boolean;
  breakdown?: Array<{
    label: string;
    value?: number;
    unit: string;
    trend?: TrendData;
  }>;
  isLoading?: boolean;
}

const MetricCard = ({
  title,
  value,
  unit,
  trend,
  hideTrends = false,
  breakdown,
  isLoading = false,
}: MetricCardProps) => {
  const theme = useTheme();
  const { dateRange, bucketOptions } = useDashboard();
  const hasBreakdown = (breakdown ?? []).length > 0;

  const calculatePriorPeriodDates = () => {
    const startDate = dayjs(dateRange.start);
    const endDate = dayjs(dateRange.end);
    const durationInMs = endDate.diff(startDate);

    const priorStartDate = startDate.subtract(durationInMs, 'millisecond');
    const priorEndDate = endDate.subtract(durationInMs, 'millisecond');

    return { priorStartDate, priorEndDate };
  };

  const TrendIndicator = ({ trendData }: { trendData?: TrendData }) => {
    if (!trendData) return null;

    const { percentageChange, isIncreasing } = trendData;
    const absChange = Math.abs(percentageChange);

    const getTooltipText = () => {
      const { priorStartDate, priorEndDate } = calculatePriorPeriodDates();
      const direction = isIncreasing ? 'increase' : 'decrease';
      const changeText =
        absChange < 0.1
          ? 'No significant change'
          : `${absChange.toFixed(1)}% ${direction}`;

      const priorPeriodText = `${formatDateByBucketUnit(
        priorStartDate.toDate(),
        bucketOptions.unit,
        true
      )} - ${formatDateByBucketUnit(
        priorEndDate.toDate(),
        bucketOptions.unit,
        true
      )}`;

      return `${changeText} compared to prior period (${priorPeriodText})`;
    };

    // If change is very small (less than 0.1%), show neutral
    if (absChange < 0.1) {
      return (
        <Tooltip title={getTooltipText()} arrow placement='top'>
          <Box
            component='span'
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              ml: 1,
              color: theme.palette.text.secondary,
              cursor: 'help',
            }}
          >
            <Remove fontSize='small' />
            <Typography variant='caption' sx={{ ml: 0.5 }}>
              0%
            </Typography>
          </Box>
        </Tooltip>
      );
    }

    return (
      <Tooltip title={getTooltipText()} arrow placement='top'>
        <Box
          component='span'
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            ml: 1,
            color: isIncreasing
              ? theme.palette.success.main
              : theme.palette.error.main,
            cursor: 'help',
          }}
        >
          {isIncreasing ? (
            <TrendingUp fontSize='small' />
          ) : (
            <TrendingDown fontSize='small' />
          )}
          <Typography variant='caption' sx={{ ml: 0.5 }}>
            {absChange.toFixed(1)}%
          </Typography>
        </Box>
      </Tooltip>
    );
  };

  const subheader = useMemo(() => {
    const start = formatDateByBucketUnit(
      dateRange.start,
      bucketOptions.unit,
      true
    );
    const end = formatDateByBucketUnit(dateRange.end, bucketOptions.unit, true);
    return `${start} - ${end}`;
  }, [dateRange, bucketOptions]);

  return (
    <WidgetCard
      title={title}
      subheader={subheader}
      cardContentProps={{
        sx: {
          display: 'flex',
          alignItems: hasBreakdown ? 'stretch' : 'center',
          justifyContent: hasBreakdown ? 'flex-start' : 'center',
          minHeight: hasBreakdown ? 120 : 80,
          color: lighten(theme.palette.primary.main, 0.1),
        },
      }}
    >
      {isLoading ? (
        <Box
          sx={{
            width: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            py: 2,
          }}
        >
          <CircularProgress size={24} />
        </Box>
      ) : hasBreakdown ? (
        <Box sx={{ py: 1 }}>
          {breakdown!.map((item) => (
            <Box
              key={item.label}
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 1,
                py: 0.5,
              }}
            >
              <Typography
                variant='body2'
                sx={{ color: theme.tokens.colors.mutedForeground }}
              >
                {item.label}
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Typography
                  variant='h4'
                  fontSize={`${theme.tokens.typography.fontSize.sm}px`}
                  fontWeight={'bold'}
                >
                  {item.value !== undefined ? formatNumber(item.value) : '0'}{' '}
                  {item.unit}
                </Typography>
                {!hideTrends && <TrendIndicator trendData={item.trend} />}
              </Box>
            </Box>
          ))}
        </Box>
      ) : (
        <Box
          sx={{
            textAlign: 'center',
            py: 1,
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Typography variant='h4' fontSize={'25px'} fontWeight={'bold'}>
              {value !== undefined ? formatNumber(value) : '0'} {unit}
            </Typography>
            {!hideTrends && <TrendIndicator trendData={trend} />}
          </Box>
        </Box>
      )}
    </WidgetCard>
  );
};

export default MetricCard;
